import axios from "axios";
import useAuth from "hooks/useAuth";
import useConstants from "constants";

const useAPI = () => {
  const { BASE_URL, getAccessToken, refreshAccessToken, cleanUpAndRedirect } = useAuth();
  const { SWM_API_URL } = useConstants();

  const api = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
  });

  // Add an interceptor to attach the access token to requests
  api.interceptors.request.use(
    async (config) => {
      config.headers["use-cookie-auth"] = "true"; // This header is used to indicate that the request should use cookie-based authentication
      config.headers["Content-Type"] = "application/json"; // Set the content type to JSON
      if (config.useAuth !== false) {
        const accessToken = getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add an interceptor to handle token expiration
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        (error.response.status === 401 ||
          (error.response.status === 403 && error.response.data?.code === "token_not_valid")) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          const newAccessToken = await refreshAccessToken();
          axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          cleanUpAndRedirect();
          return Promise.reject(refreshError);
        }
      }
      // After retry fails, log out if 401 occurs again
      if (error.response.status === 401) {
        cleanUpAndRedirect();
      }
      return Promise.reject(error);
    }
  );

  // SWM api
  const swm_api = axios.create({
    baseURL: SWM_API_URL,
    withCredentials: true,
  });

  swm_api.interceptors.request.use(
    async (config) => {
      const accessToken = getAccessToken();
      config.headers["use-cookie-auth"] = "true"; // This header is used to indicate that the request should use cookie-based authentication
      config.headers["Content-Type"] = "application/json"; // Set the content type to JSON
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add an interceptor to handle token expiration
  swm_api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        (error.response.status === 401 ||
          (error.response.status === 403 && error.response.data?.code === "token_not_valid")) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          const newAccessToken = await refreshAccessToken();
          axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          return swm_api(originalRequest);
        } catch (refreshError) {
          cleanUpAndRedirect();
          return Promise.reject(refreshError);
        }
      }
      // After retry fails, log out if 401 occurs again
      if (error.response.status === 401) {
        cleanUpAndRedirect();
      }

      return Promise.reject(error);
    }
  );

  return { api, swm_api };
};

export default useAPI;
